import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useCountry, convertFromFCFA } from "@/context/CountryContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { MapPin, CheckCircle2, Flame, TrendingDown, Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const fmt = (n, config) => new Intl.NumberFormat("fr-FR").format(convertFromFCFA(n, config)) + " " + config.currency;

const getCommissions = (config) => [
  { emoji: "🏪", label: "Boutique / Restaurant / Hôtel / Transport", amount: fmt(500, config) },
  { emoji: "🔧", label: "Prestataire de service (coiffure, maçon…)", amount: fmt(250, config) },
  { emoji: "🛒", label: "Produit saisi avec prix", amount: fmt(50, config) },
];

export default function AgentCandidature() {
  const { config } = useCountry();
  const COMMISSIONS = getCommissions(config);
  const [form, setForm] = useState({ name: "", email: "", phone: "", zone: "", message: "", password: "", confirmPassword: "" });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (form.password && form.password !== form.confirmPassword) {
      toast.error("Les mots de passe ne correspondent pas.");
      return;
    }
    if (form.password && form.password.length < 6) {
      toast.error("Le mot de passe doit contenir au moins 6 caractères.");
      return;
    }
    setBusy(true);
    try {
      const { confirmPassword, ...payload } = form;
      await api.post("/agent/candidature", payload);
      setSent(true);
    } catch {
      toast.error("Erreur lors de l'envoi. Réessayez ou contactez-nous par WhatsApp.");
    } finally { setBusy(false); }
  };

  if (sent) return (
    <div className="max-w-lg mx-auto px-6 py-16 text-center">
      <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
        <CheckCircle2 className="w-9 h-9 text-green-600" />
      </div>
      <h2 className="font-display text-2xl font-bold text-secondary mb-2">Candidature envoyée !</h2>
      <p className="text-muted-foreground mb-6">Notre équipe vous contacte sous 24h pour valider votre compte agent.</p>
      <Link to="/"><Button className="bg-primary hover:bg-primary/90">Retour à l'accueil</Button></Link>
    </div>
  );

  return (
    <div className="max-w-2xl mx-auto px-6 py-10">

      {/* Header */}
      <div className="flex items-center gap-3 mb-2">
        <div className="w-10 h-10 bg-primary rounded-xl flex items-center justify-center">
          <MapPin className="w-5 h-5 text-white" />
        </div>
        <div>
          <h1 className="font-display text-2xl font-bold text-secondary">Devenir Agent Terrain</h1>
          <p className="text-sm text-muted-foreground">LesMeilleursPrix — {config.cities[0]} & {config.name}</p>
        </div>
      </div>

      {/* Ticker teaser */}
      <div className="flex items-center gap-2 bg-primary/5 border border-primary/20 rounded-lg px-4 py-2.5 mb-6 mt-4">
        <Flame className="w-4 h-4 text-primary shrink-0" />
        <TrendingDown className="w-4 h-4 text-primary shrink-0" />
        <p className="text-sm text-secondary">
          Gagnez <span className="font-bold text-primary">jusqu'à {new Intl.NumberFormat("fr-FR").format(convertFromFCFA(150000, config))} {config.currency}/mois</span> en enregistrant des boutiques et prix dans votre quartier.
        </p>
      </div>

      {/* Barème */}
      <div className="bg-secondary text-white rounded-xl p-5 mb-6">
        <p className="text-xs font-bold tracking-widest uppercase text-white/50 mb-3">Votre rémunération</p>
        <div className="space-y-3">
          {COMMISSIONS.map((c) => (
            <div key={c.label} className="flex items-center gap-3">
              <span className="text-xl">{c.emoji}</span>
              <span className="flex-1 text-sm text-white/80">{c.label}</span>
              <span className="font-bold text-primary text-sm tabular-nums">{c.amount}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Ce qu'on demande */}
      <div className="grid sm:grid-cols-2 gap-3 mb-8">
        {[
          { e: "📱", t: "Un smartphone avec internet" },
          { e: "🗓️", t: "3 jours minimum par semaine" },
          { e: "📍", t: `Basé à ${config.cities[0]} ou en ${config.name}` },
          { e: "🎓", t: "Aucun diplôme requis — on vous forme" },
        ].map((i) => (
          <div key={i.t} className="flex items-center gap-3 bg-muted/40 rounded-lg px-4 py-3 text-sm text-secondary">
            <span className="text-lg">{i.e}</span>{i.t}
          </div>
        ))}
      </div>

      {/* Formulaire */}
      <div className="border border-border rounded-xl p-6">
        <h2 className="font-display font-bold text-secondary text-lg mb-4">Déposez votre candidature</h2>
        <form onSubmit={submit} className="space-y-4">
          <div><Label>Nom complet *</Label><Input value={form.name} onChange={set("name")} required placeholder="Kouassi Amani" /></div>
          <div><Label>Téléphone (WhatsApp) *</Label><Input value={form.phone} onChange={set("phone")} required placeholder={`${config.phone_prefix} 00 00 00 00`} /></div>
          <div><Label>Email</Label><Input type="email" value={form.email} onChange={set("email")} placeholder="exemple@gmail.com" /></div>
          <div>
            <Label>Zone / Quartier où vous souhaitez travailler *</Label>
            <Input value={form.zone} onChange={set("zone")} required placeholder={config.cities.slice(0, 3).join(", ") + "…"} />
          </div>
          <div>
            <Label>Pourquoi voulez-vous rejoindre l'équipe ? (optionnel)</Label>
            <textarea value={form.message} onChange={set("message")}
              className="w-full mt-1.5 px-3 py-2 rounded-md border border-input bg-background text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary/30"
              rows={3} placeholder="Quelques mots sur vous et votre motivation…" />
          </div>

          <div className="border-t pt-4 mt-2">
            <p className="text-xs text-muted-foreground mb-3">Choisissez un mot de passe pour accéder à votre espace agent une fois votre candidature validée.</p>
            <div className="space-y-3">
              <div>
                <Label>Mot de passe *</Label>
                <div className="relative mt-1.5">
                  <Input type={showPw ? "text" : "password"} value={form.password} onChange={set("password")} required placeholder="Minimum 6 caractères" className="pr-10" />
                  <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-secondary">
                    {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div>
                <Label>Confirmer le mot de passe *</Label>
                <Input type={showPw ? "text" : "password"} value={form.confirmPassword} onChange={set("confirmPassword")} required placeholder="Répétez votre mot de passe" className="mt-1.5" />
              </div>
            </div>
          </div>

          <Button type="submit" disabled={busy} className="w-full bg-primary hover:bg-primary/90 text-base py-5">
            {busy ? "Envoi en cours…" : "📲 Envoyer ma candidature"}
          </Button>
        </form>
        <p className="text-xs text-muted-foreground text-center mt-4">
          Notre équipe vous répond sous 24h par WhatsApp.
        </p>
      </div>

      <p className="text-center text-sm text-muted-foreground mt-6">
        Déjà agent ?{" "}
        <Link to="/login" className="text-primary font-medium hover:underline">Connectez-vous ici</Link>
      </p>
    </div>
  );
}
